// PrivacyFirstPage.jsx
// Landing Page: Privacy-First Travel Albums
// Slug: /privacy-first

import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import './LandingPages.css';

// SEO Meta Tags (set via document or react-helmet)
const SEO = {
  title: 'Privacy-First Travel Albums — On-Device Processing | VivaJour',
  description:
    'VivaJour creates travel albums using on-device processing. Album creation happens locally without uploads. Optional sharing features allow you to share selected content via secure links.',
  slug: '/privacy-first'
};

export default function PrivacyFirstPage() {
  useEffect(() => {
    document.title = SEO.title;
    // Set meta description
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.name = 'description';
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = SEO.description;
  }, []);

  return (
    <div className="landing-page">
      {/* Navigation */}
      <nav className="landing-nav">
        <div className="landing-nav-inner">
          <Link to="/" className="landing-nav-logo">
            <span>VivaJour</span>
          </Link>
          <div className="landing-nav-links">
            <Link to="/privacy-first" className="landing-nav-link active">Privacy</Link>
            <Link to="/current-trip" className="landing-nav-link">Current Trip</Link>
            <Link to="/albums" className="landing-nav-link">Albums</Link>
            <Link to="/how-it-works" className="landing-nav-link">How It Works</Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="landing-hero">
        <span className="landing-hero-badge">🔒 Privacy by Design</span>
        <h1>Privacy-First Travel Albums</h1>
        <p className="landing-hero-subtitle">
          Your photos stay on your device during album creation. Your memories stay yours.
          VivaJour uses on-device processing to create beautiful travel albums. Your photos are not uploaded for analysis or used to train AI models. When you choose to share an album, selected photos are temporarily uploaded to generate a shareable link.
        </p>
        <div className="landing-cta-group">
          <Link to="/how-it-works" className="landing-btn landing-btn-primary">
            See How It Works →
          </Link>
        </div>
        <div className="landing-trust-row">
          <span className="landing-trust-item">
            <svg viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
            No public profile required
          </span>
          <span className="landing-trust-item">
            <svg viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
            On-device processing
          </span>
        </div>
      </section>

      {/* What Privacy-First Means */}
      <section className="landing-section landing-section-alt">
        <div className="landing-container">
          <h2>What "Privacy-First" Actually Means</h2>
          <p className="landing-section-subtitle">
            Privacy-first is not a setting you toggle. It is how VivaJour is built.
          </p>

          <div className="landing-features-grid">
            <div className="landing-feature-card">
              <div className="landing-feature-icon">📱</div>
              <h3>On-Device Processing</h3>
              <p>
                Photo analysis — reading dates, GPS coordinates, and organizing trips — happens directly on your phone.
                Core album creation requires no uploads. If you choose to share an album, selected photos are temporarily
                uploaded to secure cloud storage (Firebase / Google Cloud) to generate a shareable link.
              </p>
              <p className="landing-step-note">
                Place names may be resolved using your device&apos;s built-in services or third-party mapping/geocoding
                providers used by your operating system (depending on platform and availability). VivaJour does not operate
                its own geocoding servers.
              </p>
            </div>

            <div className="landing-feature-card">
              <div className="landing-feature-icon">🚫</div>
              <h3>No Uploads for Album Creation</h3>
              <p>
                Your images stay in your camera roll during album creation. VivaJour reads photo metadata locally and builds
                albums on your device. Optional sharing features allow you to upload selected photos to generate shareable links.
              </p>
            </div>

            <div className="landing-feature-card">
              <div className="landing-feature-icon">🤖</div>
              <h3>No AI Training on Your Photos</h3>
              <p>
                We do not collect images to improve models. Your memories are not training data for advertising, profiling,
                or machine learning.
              </p>
            </div>

            <div className="landing-feature-card">
              <div className="landing-feature-icon">🎛️</div>
              <h3>You Control Your Data</h3>
              <p>
                Export, share, or delete — you decide what happens to every album you create. Shared content can be unshared
                at any time.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Sharing Transparency Section - NEW */}
      <section className="landing-section">
        <div className="landing-container-narrow">
          <div className="landing-highlight-box" style={{ border: '2px solid #0ea5e9', backgroundColor: '#f0f9ff' }}>
            <h3 style={{ fontSize: '20px', marginBottom: '16px', color: '#0369a1' }}>🔗 Optional Sharing Feature</h3>
            <p style={{ fontSize: '16px', lineHeight: '1.8', color: '#334155' }}>
              <strong>Album creation happens entirely on your device with zero uploads.</strong> If you choose to share an album via link,
              VivaJour temporarily uploads your selected photos to secure cloud storage (Firebase / Google Cloud) to generate a shareable URL.
              You control what gets shared, and you can delete shared albums at any time.
            </p>
            <p style={{ marginTop: '12px', fontSize: '15px', color: '#64748b' }}>
              This is completely optional — you can create and export albums as PDFs without ever sharing online.
            </p>
            <p style={{ marginTop: '12px', fontSize: '14px', color: '#64748b' }}>
              Learn more in our <Link to="/privacy-policy" style={{ color: '#0ea5e9', fontWeight: 600 }}>Privacy Policy</Link>
            </p>
          </div>
        </div>
      </section>

      {/* Why Privacy Matters */}
      <section className="landing-section landing-section-alt">
        <div className="landing-container">
          <h2>Why Travel Photos Deserve Privacy</h2>
          <p className="landing-section-subtitle">
            Your camera roll reveals more than you think:
          </p>

          <div className="landing-highlight-box">
            <ul>
              <li>
                <span style={{ color: '#f59e0b', fontWeight: 700 }}>📍</span>
                <span><strong>Location history</strong> — GPS coordinates show exactly where you have been</span>
              </li>
              <li>
                <span style={{ color: '#f59e0b', fontWeight: 700 }}>🕐</span>
                <span><strong>Travel patterns</strong> — timestamps reveal when you are home and when you are away</span>
              </li>
              <li>
                <span style={{ color: '#f59e0b', fontWeight: 700 }}>👥</span>
                <span><strong>Relationships</strong> — group photos reflect who you spend time with</span>
              </li>
              <li>
                <span style={{ color: '#f59e0b', fontWeight: 700 }}>📸</span>
                <span><strong>Personal moments</strong> — images never meant for public sharing</span>
              </li>
            </ul>
          </div>

          <p style={{ color: 'var(--landing-muted)', marginTop: '24px', fontSize: '15px', lineHeight: '1.7' }}>
            Many photo apps upload images to cloud servers by default, where they may be stored indefinitely, scanned for analytics,
            or used to train machine-learning models — often buried in terms of service.
          </p>
          <p style={{ color: 'var(--landing-text)', marginTop: '12px', fontSize: '16px', fontWeight: 600 }}>
            VivaJour processes photos locally for album creation. Your photos only leave your device when you explicitly choose to share an album via link.
          </p>
        </div>
      </section>

      {/* How It Works */}
      <section className="landing-section">
        <div className="landing-container">
          <h2>How It Works — Designed for Local Processing</h2>

          <div className="landing-steps">
            <div className="landing-step">
              <div className="landing-step-number">1</div>
              <div className="landing-step-content">
                <h3>Select Trip Photos</h3>
                <p>Choose photos manually or let VivaJour detect trips using date and GPS metadata already embedded in your images.</p>
              </div>
            </div>

            <div className="landing-step">
              <div className="landing-step-number">2</div>
              <div className="landing-step-content">
                <h3>Local Intelligence Builds Your Story</h3>
                <p>On-device processing groups photos by date and GPS proximity, builds a narrative structure, and suggests layouts.</p>
                <p className="landing-step-note">
                  Some place names may be resolved using cached or online system services and/or third-party mapping/geocoding providers used by your operating system.
                  This is handled by iOS/Android and its providers, not VivaJour-operated servers.
                </p>
              </div>
            </div>

            <div className="landing-step">
              <div className="landing-step-number">3</div>
              <div className="landing-step-content">
                <h3>Edit and Export — All Local</h3>
                <p>Refine layouts, add captions, and export a professional PDF — all without cloud uploads.</p>
              </div>
            </div>

            <div className="landing-step">
              <div className="landing-step-number">4</div>
              <div className="landing-step-content">
                <h3>Share (Optional)</h3>
                <p>
                  Want to share your album online? You can generate a shareable link, which temporarily uploads your selected photos to secure storage
                  (Firebase / Google Cloud). You maintain full control and can unshare at any time.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="landing-section">
        <div className="landing-container">
          <h2>VivaJour vs. Cloud-Based Photo Apps</h2>

          <div className="landing-table-wrapper">
            <table className="landing-table">
              <thead>
                <tr>
                  <th>Feature</th>
                  <th>VivaJour</th>
                  <th>Typical Photo Apps</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Photo processing</td>
                  <td className="check">On your device</td>
                  <td className="cross">Cloud servers</td>
                </tr>
                <tr>
                  <td>Photo uploads required</td>
                  <td className="check">No</td>
                  <td className="cross">Yes</td>
                </tr>
                <tr>
                  <td>Core album creation</td>
                  <td className="check">Offline</td>
                  <td className="cross">Requires internet</td>
                </tr>
                <tr>
                  <td>Place name resolution</td>
                  <td className="neutral">OS-level and/or provider (cached/online)</td>
                  <td className="cross">Cloud-based</td>
                </tr>
                <tr>
                  <td>AI training on user photos</td>
                  <td className="check">Never</td>
                  <td className="cross">Often unclear</td>
                </tr>
                <tr>
                  <td>Account required</td>
                  <td className="check">No (basic use)</td>
                  <td className="cross">Usually</td>
                </tr>
                <tr>
                  <td>Data ownership</td>
                  <td className="check">User-owned (Photos & PDFs)</td>
                  <td className="cross">Platform-controlled</td>
                </tr>
                <tr>
                  <td>Privacy as core design</td>
                  <td className="check">Yes</td>
                  <td className="cross">Rarely</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* What We Don't Do */}
      <section className="landing-section">
        <div className="landing-container-narrow">
          <h2 style={{ textAlign: 'center' }}>What We Do Not Do</h2>

          <ul className="landing-list negative" style={{ marginTop: '32px' }}>
            <li>Upload your photos automatically (uploads only occur when you choose to share)</li>
            <li>Scan images for advertising profiles</li>
            <li>Train AI models on your memories</li>
            <li>Store your photo library on our servers</li>
            <li>Sell behavioral or location data</li>
            <li>Require a public profile for basic album creation</li>
            <li>Sell your personal data to third parties</li>
          </ul>

          <p style={{ textAlign: 'center', marginTop: '32px', fontSize: '18px', fontWeight: 600, color: 'var(--landing-text)' }}>
            Your travel story exists for you, not for analytics.
          </p>
        </div>
      </section>

      {/* Fast Without Surveillance */}
      <section className="landing-section landing-section-alt">
        <div className="landing-container">
          <h2>Fast Without Surveillance</h2>
          <p className="landing-section-subtitle">
            Many apps rely on cloud AI to feel fast. VivaJour is fast because it is efficient by design, not because it sends your data elsewhere.
          </p>

          <ul className="landing-list">
            <li>Album creation in minutes</li>
            <li>No upload wait times for local processing</li>
            <li>Minimal background data usage</li>
            <li>Full editing control on your device</li>
          </ul>

          <p style={{ marginTop: '24px', fontSize: '16px', fontWeight: 600, color: 'var(--landing-text)' }}>
            Privacy does not slow you down. It protects you.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="landing-section">
        <div className="landing-container-narrow">
          <h2 style={{ textAlign: 'center' }}>Frequently Asked Questions</h2>

          <div className="landing-faq">
            <div className="landing-faq-item">
              <h4 className="landing-faq-question">How does VivaJour work without cloud processing?</h4>
              <p className="landing-faq-answer">
                VivaJour uses on-device processing to read photo metadata (dates and GPS coordinates) and organize trips. All core album creation runs locally on your device.
              </p>
            </div>

            <div className="landing-faq-item">
              <h4 className="landing-faq-question">Do you ever see my photos?</h4>
              <p className="landing-faq-answer">
                Not during album creation. Photos are processed on your device. We only receive photos you explicitly choose to share via link, which are temporarily stored on secure cloud storage to enable sharing.
                We never analyze your photos for advertising, AI training, or behavioral profiling.
              </p>
            </div>

            <div className="landing-faq-item">
              <h4 className="landing-faq-question">Is my data sold to third parties?</h4>
              <p className="landing-faq-answer">
                No. We do not collect your photo data for sale, and we do not engage in behavioral tracking or advertising. When you share albums, they are stored securely but never sold or used for marketing purposes.
              </p>
            </div>

            <div className="landing-faq-item">
              <h4 className="landing-faq-question">Can I use VivaJour offline?</h4>
              <p className="landing-faq-answer">
                Yes. Core features — photo selection, trip organization, and album building — work offline. Some place names may be resolved using cached or online system services and/or third-party mapping/geocoding providers used by your operating system. Sharing features require internet connectivity.
              </p>
            </div>

            <div className="landing-faq-item">
              <h4 className="landing-faq-question">What happens to shared albums?</h4>
              <p className="landing-faq-answer">
                When you share an album, selected photos are temporarily uploaded to Firebase Storage (Google Cloud) and remain accessible via a unique link until you choose to unshare.
                You can delete shared albums at any time from the app. When you delete a shared link, all associated album data (including photos and metadata) is immediately and permanently deleted from our servers.
              </p>
            </div>
            
<div className="landing-faq-item">

            <h4 className="landing-faq-question">What's the preview/story feature?</h4>
<p className="landing-faq-answer">
  You can preview albums as Instagram-style stories before sharing. Like sharing, this temporarily uploads selected photos to generate the preview link.
</p>
</div>
            <div className="landing-faq-item">
              <h4 className="landing-faq-question">What happens if VivaJour shuts down?</h4>
              <p className="landing-faq-answer">
                Your original photos remain in your camera roll. Exported albums stay on your device. You retain full access to your memories.
                Any albums you have shared online would become inaccessible if our servers shut down, which is why we recommend downloading PDFs of albums you want to preserve.
              </p>
            </div>

            <div className="landing-faq-item">
              <h4 className="landing-faq-question">Do I need an account to use VivaJour?</h4>
              <p className="landing-faq-answer">
                No public profile is required for basic use. To access Pro features (unlimited albums, advanced templates, sharing), you will need to use an email address and subscribe.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="landing-section landing-section-alt">
        <div className="landing-container">
          <div className="landing-callout">
            <h3>Build Your Travel Story — Privately</h3>
            <p>On-device processing. Privacy by default. Sharing by choice.</p>
            <div className="landing-cta-group">
              <a href="https://apps.apple.com/app/id6756980207" className="landing-btn landing-btn-primary" target="_blank" rel="noopener noreferrer">
                Start Free Trial on iOS
              </a>
              <a href="https://play.google.com/store/apps/details?id=com.yourco.traveltracker" className="landing-btn landing-btn-primary" target="_blank" rel="noopener noreferrer">
                Start Free Trial on Android
              </a>
            </div>
          </div>

          {/* Internal Links */}
          <div className="landing-internal-links">
            <Link to="/current-trip" className="landing-internal-link">
              📍 Current Trip Mode — Build albums while you travel
            </Link>
            <Link to="/how-it-works" className="landing-internal-link">
              ⚙️ How It Works — See the album creation process
            </Link>
            <Link to="/templates" className="landing-internal-link">
              🎨 Templates & Export — Professional layouts and PDF export
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="landing-footer-links">
          <Link to="/privacy-first" className="landing-footer-link">Privacy</Link>
          <Link to="/current-trip" className="landing-footer-link">Current Trip</Link>
          <Link to="/albums" className="landing-footer-link">Albums</Link>
          <Link to="/how-it-works" className="landing-footer-link">How It Works</Link>
          <Link to="/templates" className="landing-footer-link">Templates</Link>
        </div>
        <p className="landing-footer-meta">
          VivaJour Labs LLC • Built in Wisconsin
        </p>
        <p className="landing-footer-meta" style={{ marginTop: '8px' }}>
          <Link to="/privacy-policy" className="landing-footer-link">Privacy Policy</Link>
          {' • '}
          <Link to="/terms" className="landing-footer-link">Terms of Service</Link>
          {' • '}
          <Link to="/contact" className="landing-footer-link">Contact</Link>
        </p>
        <p className="landing-footer-tagline">
          © 2026 VivaJour. Your photos stay on your device during album creation. Your memories stay yours.
        </p>
      </footer>
    </div>
  );
}
